import { Modal } from '@mantine/core';
import type { DomainMappingResponse } from '@xver/apis/v1/kufer/domain-mappings/model';
import { Button } from '@xver/components/Atoms';
import { useTranslation } from 'react-i18next';

import DomainRecordsSections from './DomainRecordsSections';

interface ViewDomainRecordsModalProps {
    mapping: DomainMappingResponse | null;
    opened: boolean;
    onClose: () => void;
}

export default function ViewDomainRecordsModal({
    mapping,
    opened,
    onClose,
}: ViewDomainRecordsModalProps) {
    const { t } = useTranslation('containerEngineInstance');

    return (
        <Modal
            size="md"
            opened={opened}
            onClose={onClose}
            withCloseButton={false}
            overlayProps={{ backgroundOpacity: 0.55, blur: 3 }}
            transitionProps={{ transition: 'fade', duration: 128, timingFunction: 'linear' }}
            centered
        >
            {mapping && (
                <div className="flex flex-col justify-start items-start gap-4">
                    <div className="flex flex-col justify-start items-start gap-1">
                        <Modal.Title>
                            <span className="text-base font-bold">
                                {t('domains.recordsModalTitle', {
                                    domain: mapping.domain_name,
                                    defaultValue: 'Set up {{domain}}',
                                })}
                            </span>
                        </Modal.Title>
                        <p className="text-sm text-fg/68">
                            {t(
                                'domains.createdRecordHint',
                                "Add the records below at your DNS provider. Names are relative to your domain's zone — don't use @."
                            )}
                        </p>
                        {mapping.dns_mode === 'proxied' && (
                            <p className="text-sm" style={{ color: 'var(--color-status-info)' }}>
                                {t(
                                    'domains.proxiedHint',
                                    'Cloudflare proxy detected — set SSL/TLS mode to "Full". "Flexible" causes a redirect loop; use "Full (strict)" only after the first certificate has been issued.'
                                )}
                            </p>
                        )}
                        {mapping.dns_mode === 'unpointed' && (
                            <p className="text-sm" style={{ color: 'var(--color-status-warning)' }}>
                                {t(
                                    'domains.unpointedHint',
                                    "Your domain doesn't point at the platform yet — add record 2 below."
                                )}
                            </p>
                        )}
                    </div>
                    <DomainRecordsSections
                        domainName={mapping.domain_name}
                        txtRecordName={mapping.txt_record_name}
                        txtRecord={mapping.txt_record}
                        dnsTarget={mapping.dns_target}
                        dnsRecordType={mapping.dns_record_type}
                    />
                    <Button width="100%" onClick={onClose}>
                        {t('domains.done', 'Done')}
                    </Button>
                </div>
            )}
        </Modal>
    );
}
